<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { AdminRow } from '@/api/modules/admins'
import { createAdmin, deleteAdmin, fetchAdmins, patchAdmin } from '@/api/modules/admins'
import { useUserStore } from '@/stores/user'
import { formatEpoch } from '@/utils/format'

const userStore = useUserStore()

const loading = ref(false)
const rows = ref<AdminRow[]>([])

const dlg = ref(false)
const dlgMode = ref<'add' | 'edit'>('add')
const editId = ref<number | null>(null)
const saving = ref(false)

const form = reactive({
  username: '',
  password: '',
  gid: 1,
  status: 1,
})

function fmtEpoch(sec?: number | null) {
  return formatEpoch(sec, '—')
}

function statusLabel(s: number) {
  return Number(s) === 1 ? '正常' : '禁用'
}

async function load() {
  loading.value = true
  try {
    const { data } = await fetchAdmins()
    rows.value = Array.isArray(data) ? data : []
  } finally {
    loading.value = false
  }
}

function openAdd() {
  dlgMode.value = 'add'
  editId.value = null
  form.username = ''
  form.password = ''
  form.gid = 1
  form.status = 1
  dlg.value = true
}

function openEdit(row: AdminRow) {
  dlgMode.value = 'edit'
  editId.value = row.id
  form.username = row.username
  form.password = ''
  form.gid = row.gid ?? 1
  form.status = row.status ?? 1
  dlg.value = true
}

async function submitDlg() {
  if (!form.username.trim()) {
    ElMessage.warning('请填写管理员用户名')
    return
  }
  if (dlgMode.value === 'add' && !form.password) {
    ElMessage.warning('新建账号必须设置密码')
    return
  }
  saving.value = true
  try {
    if (dlgMode.value === 'add') {
      await createAdmin({
        username: form.username.trim(),
        password: form.password,
        gid: form.gid,
        status: form.status,
      })
      ElMessage.success('已添加')
    } else if (editId.value != null) {
      const patch: Record<string, unknown> = {
        username: form.username.trim(),
        gid: form.gid,
        status: form.status,
      }
      if (form.password) patch.password = form.password
      await patchAdmin(editId.value, patch)
      ElMessage.success('已保存')
    }
    dlg.value = false
    load()
  } catch {
    /* axios interceptor */
  } finally {
    saving.value = false
  }
}

async function onDelete(row: AdminRow) {
  if (row.username === userStore.username) {
    ElMessage.warning('不能删除当前登录账号')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定删除管理员「${row.username}」（ID ${row.id}）？此操作不可撤销。`,
      '删除确认',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
    )
    await deleteAdmin(row.id)
    ElMessage.success('已删除')
    await load()
  } catch (e) {
    if (e !== 'cancel') {
      /* interceptor / 4xx */
    }
  }
}

onMounted(load)
</script>

<template>
  <PageChrome title="管理员" description="对应旧版 Admin 模块；口令为 md5(adminKey + md5(明文))，与登录一致。">
    <template #actions>
      <el-space wrap>
        <el-button type="primary" @click="openAdd">添加管理员</el-button>
        <el-button :loading="loading" @click="load">刷新</el-button>
      </el-space>
    </template>

    <el-card shadow="hover" class="ep-table-scope adm-card">
      <el-table v-loading="loading" :data="rows" stripe border size="default">
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column prop="username" label="管理员账号" min-width="140" />
        <el-table-column label="添加时间" width="178" align="center">
          <template #default="{ row }">{{ fmtEpoch(row.addtime) }}</template>
        </el-table-column>
        <el-table-column label="最近登录" width="178" align="center">
          <template #default="{ row }">{{ fmtEpoch(row.lastlogin) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ statusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="gid" label="GID" width="72" align="center" />
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="openEdit(row)">编辑</el-button>
            <el-button
              type="danger"
              link
              :disabled="row.username === userStore.username"
              @click="onDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="adm-foot">共 {{ rows.length }} 条</div>
    </el-card>

    <el-dialog
      v-model="dlg"
      :title="dlgMode === 'add' ? '添加管理员' : '编辑管理员'"
      width="460px"
      destroy-on-close
    >
      <el-form label-width="100px">
        <el-form-item label="用户名" required>
          <el-input v-model="form.username" maxlength="48" autocomplete="off" />
        </el-form-item>
        <el-form-item :label="dlgMode === 'add' ? '密码' : '新密码'" :required="dlgMode === 'add'">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            maxlength="72"
            autocomplete="new-password"
            :placeholder="dlgMode === 'edit' ? '留空则不修改' : ''"
          />
        </el-form-item>
        <el-form-item label="GID">
          <el-input-number v-model="form.gid" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dlg = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitDlg">保存</el-button>
      </template>
    </el-dialog>
  </PageChrome>
</template>

<style scoped lang="scss">
.adm-card {
  border-radius: var(--ep-radius-lg) !important;
}
.adm-foot {
  margin-top: 14px;
  font-size: 13px;
  color: var(--ep-text-muted);
}
</style>
